import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

type AffiliateReferralCredit = {
  id: number;
  createdAt: number;
  affiliateReferralId: number;
  commissionAmountCents: number;
};

const styles = {
  promoCode: {
    px: 2,
    py: 1,
    display: "flex",
    border: "2px dashed",
    borderColor: "secondary.main",
    backgroundColor: "secondary.light",
    borderRadius: 2,
    cursor: "pointer",
    maxWidth: 400,
  },
  code: {
    flexGrow: 1,
    mr: 4,
  },
  balance: {
    backgroundColor: "secondary.light",
    p: 3,
    borderRadius: 3,
    maxWidth: 600,
  },
};

export default function AffiliatePartnerView({
  affiliateOrg,
}: {
  affiliateOrg: AffiliateOrganization;
}) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [affiliateCredits, setAffiliateCredits] = useState<
    AffiliateReferralCredit[]
  >([]);
  const [balance, setBalance] = useState<number>(0);
  const [viewCredits, setViewCredits] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchAffiliateBalance = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations/balance`,
      );
      setBalance(res.balance);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTransactions = async (creditId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/affiliate-organizations/credits?per_page=10`;
      if (creditId) {
        url += `&cursor=${creditId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setAffiliateCredits((prev) => [...prev, ...res.credits]);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCopyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);

    trackEvent("Affiliate Promo Code Link Copied to Clipboard");

    if (setAlert) setAlert("Copied to clipboard!", "success");
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchAffiliateBalance();
      fetchTransactions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const formatBalance = (balance: number) => {
    return Number(balance / 100)?.toFixed(2);
  };

  return (
    <Grid container columnGap={2} rowGap={2} alignItems="flex-start">
      <Grid item xs={12} lg>
        <Typography variant="h4" paragraph>
          Welcome to Bento's Affiliate Program
        </Typography>

        <Typography sx={{ mr: 4 }}>
          Earn 5% commission on every payment made by a new Bento user who
          subscribes using your unique promo code - valid for their first 12
          months.
          <br />
          <li>Refer 5 paying users and earn an additional $50</li>
          <li>Refer 10 paying users and earn an additional $150</li>
          <li>Refer 20 paying users and earn an additional $500</li>
          Promote Bento, get rewarded.{" "}
          <Link
            target="_blank"
            rel="noreferrer"
            href="https://docs.google.com/document/d/1wEM_vnE6O3rntvt7FRGjq8H5IPnnK6vWqQ9L7h3H9Ew/edit?usp=sharing"
            sx={{ color: "primary.main", cursor: "pointer" }}
            onClick={() => trackEvent("Affiliate Terms and Conditions Clicked")}
          >
            T&Cs apply.
          </Link>
        </Typography>

        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          Share your unique affiliate promotion code. This code can be used by
          Bento users to get 10% off their first purchase. You can either share
          this link:
        </Typography>
        <Box
          sx={styles.promoCode}
          onClick={() => handleCopyToClipboard(affiliateOrg.referralLink)}
        >
          <Box sx={styles.code}>{affiliateOrg.referralLink}</Box>
          <Box
            component="i"
            sx={{ lineHeight: "1.25" }}
            className="fa-regular fa-copy fa-lg"
          />
        </Box>
        <Typography
          gutterBottom
          component="div"
          sx={{ mt: 2 }}
          variant="caption"
          color="textSecondary"
        >
          Or you can share a coupon code:
        </Typography>
        <Box
          sx={styles.promoCode}
          onClick={() =>
            handleCopyToClipboard(affiliateOrg.stripePromotionCode)
          }
        >
          <Box sx={styles.code}>{affiliateOrg.stripePromotionCode}</Box>
          <Box
            component="i"
            sx={{ lineHeight: "1.25" }}
            className="fa-regular fa-copy fa-lg"
          />
        </Box>
      </Grid>

      <Grid item xs={12} lg>
        <Grid item sx={styles.balance}>
          <Typography variant="h6">💰 Current Balance</Typography>
          <FormHelperText>
            This balance will be paid in accordance to the{" "}
            <Link
              target="_blank"
              rel="noreferrer"
              href="https://docs.google.com/document/d/1wEM_vnE6O3rntvt7FRGjq8H5IPnnK6vWqQ9L7h3H9Ew/edit?usp=sharing"
              sx={{ color: "primary.main", cursor: "pointer" }}
              onClick={() =>
                trackEvent("Affiliate Terms and Conditions Clicked")
              }
            >
              T&Cs
            </Link>
            . You must have a minimum of $100 in commissions earned before being
            eligible for a payout.
          </FormHelperText>
          {fetchLoading ? (
            <Skeleton height={100} width={"50%"} />
          ) : (
            <Typography variant="h2" sx={{ my: 2 }}>
              ${balance ? formatBalance(balance) : "0.00"} USD
            </Typography>
          )}

          {affiliateCredits?.length > 0 && (
            <Link
              sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
              onClick={() => {
                setViewCredits((prev) => !prev);
                trackEvent("My Affiliate Credits Button Clicked");
              }}
            >
              <strong>{viewCredits ? "Hide" : "See Breakdown"}</strong>
            </Link>
          )}

          {viewCredits && affiliateCredits && (
            <Table size="small" sx={{ maxWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Commission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiliateCredits.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      {formatEpochTimestamp({
                        epochTimestamp: row.createdAt,
                        addYear: true,
                      })}
                    </TableCell>
                    <TableCell>User paid an invoice</TableCell>
                    <TableCell>
                      ${formatBalance(row.commissionAmountCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > affiliateCredits?.length && viewCredits && (
            <Button
              onClick={() =>
                fetchTransactions(
                  affiliateCredits?.[affiliateCredits?.length - 1]?.id,
                )
              }
              size="small"
              sx={{ fontSize: 12, textTransform: "none" }}
              color="secondary"
            >
              View more
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
